import { Close, OpenInNew } from "@mui/icons-material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Alert,
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { useVendorPortal } from "./VendorPortalLayout";

const AlertsComponent: React.FC = () => {
  const { handleUnauthorizedError, isSessionReady, token } = useVendorPortal();

  const [alerts, setAlerts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>("All");

  useEffect(() => {
    // Only run when session is ready
    if (!isSessionReady) return;

    if (token) {
      // If token exists, use it to fetch data
      fetchVendorPortalAlerts(token);
    } else {
      // If no token, show error
      toast.error(<CustomToast message="Session expired. Please log in again." />);
    }
  }, [isSessionReady, token]); // Depend on isSessionReady and token

  const fetchVendorPortalAlerts = (token: string) => {
    useFetch(API_ENDPOINTS.VENDOR_PORTAL_ALERTS.url, "GET", {
      failureMessage: API_ENDPOINTS.VENDOR_PORTAL_ALERTS.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        if (response.data.details && Array.isArray(response.data.details)) {
          setAlerts(response.data.details);
        }
      },
      catchCallBack: (error) => {
        handleUnauthorizedError(error);
      },
    });
  };

  const criticalCount = alerts.filter((alert) => alert?.isRejected).length;
  const actionRequiredCount = alerts.filter((alert) => !alert?.isRejected).length;

  const getFilteredAlerts = () => {
    switch (filterType) {
      case "Critical":
        return alerts.filter((alert) => alert?.isRejected);
      case "Action Required":
        return alerts.filter((alert) => !alert?.isRejected);
      default:
        return alerts;
    }
  };

  const handleViewAll = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFilterType("All");
  };

  // Optimized chip component
  const FilterChip = ({ label, filterValue }: { label: string; filterValue: string }) => (
    <Chip
      label={label}
      onClick={() => setFilterType(filterValue)}
      variant={filterType === filterValue ? "filled" : "outlined"}
      size="small"
      sx={{
        backgroundColor: filterType === filterValue ? "#EEE8ED" : "transparent",
        color: filterType === filterValue ? "#333" : "#666",
        border: filterType === filterValue ? "1px solid #EEE8ED" : "1px solid #ccc",
        borderRadius: "16px",
        fontSize: "12px",
        height: "28px",
        "&:hover": {
          backgroundColor: filterType === filterValue ? "#E5DDE1" : "#f5f5f5",
        },
      }}
    />
  );

  return (
    <>
      {/* Main Alert Card based on Figma design */}
      {alerts.length > 0 && (
        <Card
          sx={{
            backgroundColor: "#fff8e1",
            border: "1px solid #f57f17",
            borderRadius: "8px",
            position: "relative",
            m: 2,
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {/* Left section with icon and content */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                {/* Icon with badge */}
                <Box sx={{ position: "relative" }}>
                  {alerts.length > 0 && (
                    <Badge
                      badgeContent={alerts.length}
                      color="error"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "8px",
                          height: "12px",
                          minWidth: "12px",
                          borderRadius: "6px",
                          padding: "0 2px",
                          top: 6,
                          right: 6,
                        },
                      }}
                    >
                      <NotificationsIcon />
                    </Badge>
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "rgba(0,0,0,0.87)",
                      lineHeight: 1.3,
                    }}
                  >
                    Balance Confirmation Alerts
                  </Typography>

                  {/* Status indicators */}
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {/* Critical count */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          backgroundColor: "#d32f2f",
                          borderRadius: "50%",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "rgba(0,0,0,0.6)",
                          lineHeight: 1.3,
                        }}
                      >
                        {criticalCount} Critical
                      </Typography>
                    </Box>

                    {/* Action Required count */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          backgroundColor: "#ef6c00",
                          borderRadius: "50%",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "rgba(0,0,0,0.6)",
                          lineHeight: 1.3,
                        }}
                      >
                        {actionRequiredCount} Action Required
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Right section with buttons */}
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <CustomButton
                  variant="outlined"
                  size="small"
                  onClick={handleViewAll}
                  sx={{
                    borderColor: "rgba(239,108,0,0.5)",
                    color: "#ef6c00",
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                    minHeight: "32px",
                    minWidth: "70px",
                  }}
                  endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                >
                  View all
                </CustomButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modal for detailed alerts */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth={DIALOG_MAX_WIDTHS.MD} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">All Notification ({alerts.length})</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleCloseModal}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {/* Filter chips */}
          <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
            <FilterChip label="All" filterValue="All" />
            <FilterChip label="Critical" filterValue="Critical" />
            <FilterChip label="Action Required" filterValue="Action Required" />
          </Box>

          {/* Alert list */}
          <Stack spacing={2}>
            {getFilteredAlerts().map((alert, index) => (
              <Alert
                key={`modal-alert-${index}`}
                severity={alert?.isRejected ? "error" : "warning"}
                sx={{
                  alignItems: "flex-start",
                  "& .MuiAlert-message": {
                    width: "100%",
                  },
                }}
                action={
                  <CustomButton
                    variant="contained"
                    color={alert?.isRejected ? "error" : "warning"}
                    size="small"
                    onClick={() => {
                      if (alert.portalLink) {
                        window.open(alert.portalLink, "_blank", "noopener,noreferrer");
                      }
                    }}
                    sx={{ mt: 1, minWidth: "150px" }}
                  >
                    {alert?.isRejected ? "Update Response" : "Submit Response"}
                  </CustomButton>
                }
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {alert?.isRejected
                      ? `Incorrect Ledger Submitted - ${alert?.rejectedReason || "Require ledger since inception"}`
                      : "Action Required"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {alert.subject}
                  </Typography>
                </Box>
              </Alert>
            ))}

            {getFilteredAlerts().length === 0 && (
              <Typography variant="body2" sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                No alerts found for the selected filter.
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AlertsComponent;
